// Single shared money formatter. Lived in pages/finance/LineItemsCard.tsx
// after the invoice-totals fix consolidated the drawers' copies; moved here so
// non-finance surfaces (HR payroll) never import a finance component for a
// number format. Five finance pages still carry private copies — migrating
// them is deliberate follow-up cleanup, not drive-by.
// currencySymbol (SB-9): the ONE source of the symbol-or-code rule — £/$/€
// for the three symboled majors, the CODE itself for everything else
// (matching money()'s self-prefixing render; a made-up glyph would be a lie).
// Callers deciding whether they hold a symbol or a code can compare the
// result to the input: sym === code ⇔ no symbol exists.
export function currencySymbol(code: string): string {
  return code === 'GBP' ? '£' : code === 'USD' ? '$' : code === 'EUR' ? '€' : code
}

export function money(n: number, currency = 'GBP'): string {
  // Symboled majors; any other code PREFIXES itself ("JPY 1,234.00") — with
  // the increment-3 picker offering the whole ECB set, a bare number would
  // hide which currency the figure is in (data honesty).
  const sym = currencySymbol(currency)
  return `${sym}${sym === currency ? ' ' : ''}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

// money() renders a negative as "£-190.20", putting the sign inside the
// amount where it reads as a typo. A refund is a real, normal payroll outcome
// on the cumulative basis, so it gets the accounting form: a true minus sign
// (U+2212, not a hyphen) ahead of the currency symbol. Takes integer PENCE —
// the wire's unit — unlike money()'s pounds. (HREmployees' NI panel still
// carries a private copy; migrating it is deliberate follow-up cleanup, not
// drive-by, same as the finance pages above.)
export function signedMoney(pence: number): string {
  return pence < 0 ? `−${money(Math.abs(pence) / 100)}` : money(pence / 100)
}

// The currency fence's exclusion note (FX increment 0). A fenced £ figure
// that silently omitted rows would trade one lie for another — so every
// fenced sum names what it excluded. Returns '' when nothing was excluded:
// the common all-GBP case renders NO decoration.
export function nonGbpNote(count: number | undefined, noun: string): string {
  if (!count || count <= 0) return ''
  return `+${count} non-GBP ${noun}${count === 1 ? '' : 's'} not included`
}

// withNonGbpNote appends the exclusion to an existing caption — the shape
// every FinanceMetric note slot needs. Base text unchanged when count is 0.
export function withNonGbpNote(base: string, count: number | undefined, noun: string): string {
  const note = nonGbpNote(count, noun)
  return note === '' ? base : `${base} · ${note}`
}

// FX provenance (SB-6): the caption for an INDIVIDUAL non-GBP figure whose
// captured base-GBP equivalent is shown beside it. States what the number
// actually IS — the stored capture, not an estimate — hence no "approx"
// hedging beyond the ≈ (the base is exact at capture; only its currency
// changed). NEVER attach this to an aggregate: no single rate stands behind
// a sum — aggregates carry nonGbpNote's exclusion counts instead. Returns ''
// unless BOTH halves of the capture are present (GBP rows and pre-capture
// history render no decoration).
export function fxProvenance(basePence: number | null | undefined, fxRateDate: string | null | undefined): string {
  if (basePence == null || !fxRateDate) return ''
  const d = new Date(fxRateDate)
  const date = isNaN(d.getTime())
    ? fxRateDate
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  return `≈ ${money(basePence / 100)} · converted at the rate captured on ${date}`
}

// The D9 write-side boundary: a pounds string typed into a money input →
// integer PENCE, or null when the text is not an amount (empty, malformed,
// negative, finer than a penny, or wider than the columns' NUMERIC(12,2)
// 10 integer digits). Digit-parsed — pounds·100 + two zero-padded fraction
// digits — so no user-typed float ever exists; a caller putting pence/100 on
// a pounds wire field stays exact, because any whole-pence amount's nearest
// double rounds back to the same integer server-side.
export function parsePoundsToPence(input: string): number | null {
  // Strips every symbol currencySymbol can produce, so "$18,000" parses the
  // way "£18,000" always has. (The GBP-flavoured NAME is misleading now that
  // the value is major units of the picker's currency — renaming is
  // follow-up churn, deliberately not done in SB-9.)
  const s = input.replace(/[£$€,\s]/g, '')
  if (!/^\d{1,10}(\.\d{1,2})?$/.test(s)) return null
  const [pounds, frac = ''] = s.split('.')
  return Number(pounds) * 100 + Number((frac + '00').slice(0, 2))
}

// ABSENT vs ZERO on screen, the payroll surfaces' standing rule: a figure the
// wire omits renders an em dash, a real 0 renders £0.00. Never `?? 0` — a
// coerced zero is indistinguishable from a genuine one (an NI-only row has NO
// PAYE figure; a student-loan plan below its threshold really did deduct
// nothing). Promoted here from PayrollPayRun so the pay-run table and the
// record gate cannot drift apart on what an absent figure looks like.
export function penceCell(p?: number): string {
  return p === undefined ? '—' : signedMoney(p)
}
